import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ExportDropdown, Hours, Percent, RouteLink, Widget } from '~/components';
import { ProjectRoleFiltersBar, ProjectRoleFiltersGroup } from '~/components/filters';
import ClientFiltersBar from '~/components/filters/ClientFiltersBar';
import ClientFiltersGroup from '~/components/filters/ClientFiltersGroup';
import FilterButton from '~/components/filters/FilterButton';
import MemberFiltersBar from '~/components/filters/MemberFiltersBar';
import MemberFiltersGroup from '~/components/filters/MemberFiltersGroup';
import ProjectFiltersBar from '~/components/filters/ProjectFiltersBar';
import ProjectFiltersGroup from '~/components/filters/ProjectFiltersGroup';
import ReportPeriodFilter from '~/components/filters/ReportPeriodFilter';
import TaskFiltersBar from '~/components/filters/TaskFiltersBar';
import TaskFiltersGroup from '~/components/filters/TaskFiltersGroup';
import TimeFiltersBar from '~/components/filters/TimeFiltersBar';
import TimeFiltersGroup from '~/components/filters/TimeFiltersGroup';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useDocumentTitle,
  useProjectRoleFilters,
  useSearchParams,
  useSearchParamsConfig,
  useTimeEntriesReportURL,
} from '~/hooks';
import useClientFilters from '~/hooks/useClientFilters';
import useMemberFilters from '~/hooks/useMemberFilters';
import useProjectFilters from '~/hooks/useProjectFilters';
import useTaskFilters from '~/hooks/useTaskFilters';
import useTimeFilters from '~/hooks/useTimeFilters';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort } from '~/utils';
import mimeTypes from '~/utils/mimeTypes';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Meter from '../components/Meter';
import { Metric, Metrics } from '../components/Metric';
import Report from '../components/Report';
import Table from '../components/table';
import useReportsSearchParamsConfig from '../hooks/useReportsSearchParamsConfig';
import ReportActions from '../ReportActions';

function TimeApprovalSummaryByMember({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();
  const memberFilters = useMemberFilters();
  const taskFilters = useTaskFilters();
  const roleFilters = useProjectRoleFilters();
  const timeFilters = useTimeFilters();

  const [params, setParams] = useState({
    period: null,
    sort: new QuerySort('name', 'asc'),
    ...clientFilters.filters,
    ...projectFilters.filters,
    ...memberFilters.filters,
    ...taskFilters.filters,

    ...timeFilters.filters,
  });

  const searchParamsConfig = useSearchParamsConfig();
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: reportsSearchParamsConfig.period,
        sort: { default: new QuerySort('name', 'asc'), ...searchParamsConfig.sort },
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...memberFilters.searchParamsConfig,
        ...taskFilters.searchParamsConfig,
        ...roleFilters.searchParamsConfig,
        ...timeFilters.searchParamsConfig,
      }),
      [
        searchParamsConfig,
        reportsSearchParamsConfig,
        clientFilters,
        projectFilters,
        memberFilters,
        taskFilters,
        roleFilters,
        timeFilters,
      ],
    ),
    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      sort: params.sort,
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...memberFilters.mapUrlSearchParams(params),
      ...taskFilters.mapUrlSearchParams(params),
      ...roleFilters.mapUrlSearchParams(params),
      ...timeFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, memberFilters, taskFilters, roleFilters, timeFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, status: 'sorting' }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.pdf);
                    setIsOpen(false);
                  }}>
                  Export to PDF
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <ReportPeriodFilter
          value={params.period}
          onChange={({ target: { value } }) => handleApplyFilters({ period: value })}
        />

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectRoleFiltersBar filters={params} onChange={handleApplyFilters} />

        <TaskFiltersBar filters={params} onChange={handleApplyFilters} />

        <TimeFiltersBar filters={params} onChange={handleApplyFilters} />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;

  const timeDetail = useTimeEntriesReportURL({ params });

  return (
    <>
      <Report.Summary>
        <Widget>
          <Meter>
            <Meter.Value
              label="Not Submitted"
              value={report.totals.notSubmitted}
              color={colors.black}
              tooltip={
                <>
                  <Hours value={report.totals.notSubmitted} /> hours (
                  <Percent value={report.totals.notSubmitted / report.totals.total} />)
                </>
              }
            />
            <Meter.Value
              label="Pending Approval"
              value={report.totals.pendingApproval}
              color={colors.warning}
              tooltip={
                <>
                  <Hours value={report.totals.pendingApproval} /> hours (
                  <Percent value={report.totals.pendingApproval / report.totals.total} />)
                </>
              }
            />
            <Meter.Value
              label="Approved"
              value={report.totals.approved}
              color={colors.primary}
              tooltip={
                <>
                  <Hours value={report.totals.approved} /> hours (
                  <Percent value={report.totals.approved / report.totals.total} />)
                </>
              }
            />
            <Meter.Value
              label="Rejected"
              value={report.totals.rejected}
              color={colors.danger}
              tooltip={
                <>
                  <Hours value={report.totals.rejected} /> hours (
                  <Percent value={report.totals.rejected / report.totals.total} />)
                </>
              }
            />
          </Meter>
          <Metrics>
            <Metric>
              <RouteLink to={timeDetail({ timeApprovalStatusId: 'not_submitted' })}>
                <Metric.Value>
                  <Hours value={report.totals.notSubmitted} />
                </Metric.Value>
                <Metric.Label>Not Submitted</Metric.Label>
              </RouteLink>
            </Metric>
            <Metric>
              <RouteLink to={timeDetail({ timeApprovalStatusId: 'pending_approval' })}>
                <Metric.Value>
                  <Hours value={report.totals.pendingApproval} />
                </Metric.Value>
                <Metric.Label>Pending Approval</Metric.Label>
              </RouteLink>
            </Metric>
            <Metric>
              <RouteLink to={timeDetail({ timeApprovalStatusId: 'approved' })}>
                <Metric.Value>
                  <Hours value={report.totals.approved} />
                </Metric.Value>
                <Metric.Label>Approved</Metric.Label>
              </RouteLink>
            </Metric>
            <Metric>
              <RouteLink to={timeDetail({ timeApprovalStatusId: 'rejected' })}>
                <Metric.Value>
                  <Hours value={report.totals.rejected} />
                </Metric.Value>
                <Metric.Label>Rejected</Metric.Label>
              </RouteLink>
            </Metric>
            <Metric>
              <RouteLink to={timeDetail()}>
                <Metric.Value>
                  <Hours value={report.totals.total} />
                </Metric.Value>
                <Metric.Label>Total</Metric.Label>
              </RouteLink>
            </Metric>
          </Metrics>
        </Widget>
      </Report.Summary>

      <Report.Table>
        <Table>
          <Table.Header>
            <Table.Column sticky minWidth="16rem" name="name" onSort={onSort} sort={params.sort}>
              Member
            </Table.Column>
            <Table.Column width="8rem" name="employmentTypeId" onSort={onSort} sort={params.sort}>
              Member Type
            </Table.Column>
            <Table.Column width="8rem" name="notSubmitted" align="right" onSort={onSort} sort={params.sort}>
              Not Submitted
            </Table.Column>
            <Table.Column width="8rem" name="pendingApproval" align="right" onSort={onSort} sort={params.sort}>
              Pending Approval
            </Table.Column>
            <Table.Column width="8rem" name="approved" align="right" onSort={onSort} sort={params.sort}>
              Approved
            </Table.Column>
            <Table.Column width="8rem" name="rejected" align="right" onSort={onSort} sort={params.sort}>
              Rejected
            </Table.Column>
            <Table.Column width="8rem" name="total" align="right" onSort={onSort} sort={params.sort}>
              Total
            </Table.Column>
          </Table.Header>

          <Table.Body fade={query.status === 'sorting'}>
            {report.records.map((member) => {
              return (
                <Table.Row key={member.id}>
                  <Table.Cell>{member.name}</Table.Cell>
                  <Table.Cell>{member.employmentType.name}</Table.Cell>
                  <Table.Cell>
                    <RouteLink to={timeDetail({ timeApprovalStatusId: 'not_submitted', memberId: member.id })}>
                      <Hours value={member.notSubmitted} />
                    </RouteLink>
                  </Table.Cell>
                  <Table.Cell>
                    <RouteLink to={timeDetail({ timeApprovalStatusId: 'pending_approval', memberId: member.id })}>
                      <Hours value={member.pendingApproval} />
                    </RouteLink>
                  </Table.Cell>
                  <Table.Cell>
                    <RouteLink to={timeDetail({ timeApprovalStatusId: 'approved', memberId: member.id })}>
                      <Hours value={member.approved} />
                    </RouteLink>
                  </Table.Cell>
                  <Table.Cell>
                    <RouteLink to={timeDetail({ timeApprovalStatusId: 'rejected', memberId: member.id })}>
                      <Hours value={member.rejected} />
                    </RouteLink>
                  </Table.Cell>
                  <Table.Cell>
                    <RouteLink to={timeDetail({ memberId: member.id })}>
                      <Hours value={member.total} />
                    </RouteLink>
                  </Table.Cell>
                </Table.Row>
              );
            })}

            <Table.Row style={{ fontWeight: weights.bold }}>
              <Table.Cell>Total</Table.Cell>
              <Table.Cell />
              <Table.Cell>
                <RouteLink to={timeDetail({ timeApprovalStatusId: 'not_submitted' })}>
                  <Hours value={report.totals.notSubmitted} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink to={timeDetail({ timeApprovalStatusId: 'pending_approval' })}>
                  <Hours value={report.totals.pendingApproval} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink to={timeDetail({ timeApprovalStatusId: 'approved' })}>
                  <Hours value={report.totals.approved} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink to={timeDetail({ timeApprovalStatusId: 'rejected' })}>
                  <Hours value={report.totals.rejected} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink to={timeDetail({})}>
                  <Hours value={report.totals.total} />
                </RouteLink>
              </Table.Cell>
            </Table.Row>
          </Table.Body>

          <Table.Status
            total={report.records.length}
            label="Member"
            isLoading={query.status !== 'ready'}
            partial={report.partialResults}
          />
        </Table>
      </Report.Table>
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <ReportPeriodFilter
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectRoleFiltersGroup filters={filters} onChange={handleFilter} />

      <TaskFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <TimeFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

export default TimeApprovalSummaryByMember;
